// @ts-ignore: If you don't have types for 'mqtt', install @types/mqtt or ignore this line if not needed
import mqtt, { MqttClient, IClientOptions, IClientPublishOptions } from "mqtt";

const MQTT_URL = import.meta.env.VITE_MQTT_URL;
const MQTT_USERNAME = import.meta.env.VITE_MQTT_USERNAME;
const MQTT_PASSWORD = import.meta.env.VITE_MQTT_PASSWORD;

class MQTTService {
  private static instance: MQTTService;
  private client: MqttClient | null = null;

  private constructor() {}

  public static getInstance(): MQTTService {
    if (!MQTTService.instance) {
      MQTTService.instance = new MQTTService();
    }
    return MQTTService.instance;
  }

  public connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.client && this.client.connected) {
        console.log("MQTT already connected");
        resolve();
        return;
      }

      const options: IClientOptions = {
        username: MQTT_USERNAME,
        password: MQTT_PASSWORD,
        reconnectPeriod: 2000,
        connectTimeout: 30_000,
        clean: true,
        // Unique clientId is important for multiple browser tabs & broker session
        clientId: `webclient-${Math.random().toString(16).slice(2, 10)}`,
      };

      console.log("Connecting to MQTT:", MQTT_URL, options);

      try {
        this.client = mqtt.connect(MQTT_URL, options);
      } catch (err) {
        console.error("MQTT connect threw synchronously:", err);
        reject(err);
        return;
      }

      this.client.on("connect", () => {
        console.log("MQTT connected");
        // Immediately subscribe to $SYS topics or any other default topic
        this.subscribe("$SYS/broker", (topic, message) => {
          console.log(`[$SYS] ${topic}: ${message.toString()}`);
        });
        resolve();
      });

      this.client.on("error", (err: Error) => {
        console.error("MQTT connection error:", err);
        reject(err);
      });

      this.client.on("close", () => {
        console.warn("MQTT connection closed");
      });

      this.client.on("offline", () => {
        console.warn("MQTT client went offline");
      });

      this.client.on("reconnect", () => {
        console.info("MQTT reconnecting");
      });
    });
  }

  public subscribe(
    topic: string,
    callback: (topic: string, message: string) => void
  ) {
    if (!this.client) throw new Error("MQTT client not connected");
    this.client.subscribe(topic, (err) => {
      if (err) {
        console.error(`MQTT subscribe error on ${topic}:`, err);
      } else {
        console.log(`Subscribed to ${topic}`);
      }
    });
    this.client.on("message", (msgTopic: string, message: Buffer) => {
      if (msgTopic === topic) {
        callback(msgTopic, message.toString());
      }
    });
  }

  public unsubscribe(topic: string) {
    if (!this.client) throw new Error("MQTT client not connected");
    this.client.unsubscribe(topic, (err) => {
      if (err) {
        console.error(`MQTT unsubscribe error on ${topic}:`, err);
      } else {
        console.log(`Unsubscribed from ${topic}`);
      }
    });
  }

  public publish(
    topic: string,
    message: string,
    options?: IClientPublishOptions
  ) {
    if (!this.client) throw new Error("MQTT client not connected");
    if (options) {
      this.client.publish(topic, message, options, (err) => {
        if (err) {
          console.error(`MQTT publish error to ${topic}:`, err);
        } else {
          console.log(`Published to ${topic}: ${message}`);
        }
      });
    } else {
      this.client.publish(topic, message, (err) => {
        if (err) {
          console.error(`MQTT publish error to ${topic}:`, err);
        } else {
          console.log(`Published to ${topic}: ${message}`);
        }
      });
    }
  }

  public disconnect() {
    if (this.client) {
      this.client.removeAllListeners();
      this.client.end(true); // force close and stop reconnecting
      console.info("🔌 MQTT disconnected");
      this.client = null;
    }
  }

  public isConnected(): boolean {
    return !!this.client && this.client.connected;
  }
}

export default MQTTService.getInstance();
